import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { normalizeAuthorName } from '@/lib/authorUtils';
import { toast } from 'sonner';

interface AuthorFollow {
  id: string;
  user_id: string;
  author_name: string;
  author_name_normalized: string;
  created_at: string;
  notification_enabled: boolean;
}

export function useAuthorFollows() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get all followed authors
  const { data: followedAuthors = [], isLoading } = useQuery({
    queryKey: ['author-follows', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('author_follows' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as AuthorFollow[];
    },
    enabled: !!user,
  });

  // Check if following specific author
  const isFollowing = (authorName: string) => {
    const normalized = normalizeAuthorName(authorName);
    return followedAuthors.some(f => f.author_name_normalized === normalized);
  };

  // Follow author
  const followMutation = useMutation({
    mutationFn: async (authorName: string) => {
      if (!user) throw new Error('Must be logged in');

      const normalized = normalizeAuthorName(authorName);
      
      const { error } = await supabase
        .from('author_follows' as any)
        .insert({
          user_id: user.id,
          author_name: authorName,
          author_name_normalized: normalized,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['author-follows'] });
      toast.success('Following author! You\'ll get notified of new releases.');
    },
    onError: (error: any) => {
      if (error.message.includes('duplicate')) {
        toast.error('Already following this author');
      } else {
        toast.error('Failed to follow author');
      }
    },
  });

  // Unfollow author
  const unfollowMutation = useMutation({
    mutationFn: async (authorName: string) => {
      if (!user) throw new Error('Must be logged in');

      const normalized = normalizeAuthorName(authorName);
      
      const { error } = await supabase
        .from('author_follows' as any)
        .delete()
        .eq('user_id', user.id)
        .eq('author_name_normalized', normalized);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['author-follows'] });
      toast.success('Unfollowed author');
    },
    onError: () => {
      toast.error('Failed to unfollow author');
    },
  });

  // Toggle notification preference
  const toggleNotificationMutation = useMutation({
    mutationFn: async ({ authorName, enabled }: { authorName: string; enabled: boolean }) => {
      if (!user) throw new Error('Must be logged in');

      const normalized = normalizeAuthorName(authorName);
      
      const { error } = await supabase
        .from('author_follows' as any)
        .update({ notification_enabled: enabled } as any)
        .eq('user_id', user.id)
        .eq('author_name_normalized', normalized);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['author-follows'] });
    },
  });

  return {
    followedAuthors,
    isLoading,
    isFollowing,
    followAuthor: followMutation.mutate,
    unfollowAuthor: unfollowMutation.mutate,
    toggleNotification: toggleNotificationMutation.mutate,
    isFollowingLoading: followMutation.isPending,
    isUnfollowingLoading: unfollowMutation.isPending,
  };
}
